import * as React from 'react';
import classNames from 'classnames';
import Measure from 'react-measure';

export interface OnAfterOpen {(props: PopoverProps, state: State): any}
export interface OnClose {(props: PopoverProps, state: State): any}

export interface PopoverProps {
  forceActiveState?: boolean;
  children: React.ReactNode;
  enabled?: boolean;
  open?: boolean;
  id: string;
  label: string;
  ref?: any;
  onClose?: OnClose;
  onShouldClose?: any;
  onAfterOpen?: OnAfterOpen;
  onShouldOpen?: any;
  onButtonClick?: any;
  popoverClassNames?: string[];
  onResize?: any;
  className?: string;
}

export interface State {
  popoverIsOpen: boolean;
}

const HOVER_GRACE_PERIOD = 200;

function NO_OP() {}

const style = {
  activeButton: 'shop-filters__button--active',
  button: 'shop-filters__button ui-popover__button',
  container: 'ui-popover__container',
  curtain: 'ui-popover__curtain',
  loadingButton: 'shop-filters__button--loading',
  popover: 'ui-popover',
};

export class Popover extends React.PureComponent<PopoverProps, State> {
  newHoverTimer: any = null;
  buttonRef: any = null;

  constructor(props: PopoverProps) {
    super(props);
    this.state = {
      popoverIsOpen: false,
    };
  }

  afterOpen = () => {
    const {onAfterOpen} = this.props;

    if (onAfterOpen) {
      onAfterOpen(this.props, this.state);
    }
  };

  handleResize = (contentRect) => {
    const {id, onResize = NO_OP} = this.props;

    const {
      bounds: {width},
      margin: {left, right},
    } = contentRect;

    const newWidth = width + left + right;

    onResize(id, newWidth);
  };

  clearHoverTimer = () => {
    this.newHoverTimer = null;
  };

  buttonMouseMove = (event) => {
    event.stopPropagation();

    if (this.isControlledComponent()) {
      if (this.props.open) { return; }
      const {onShouldOpen, id} = this.props;
      onShouldOpen(id);
    } else {
      if (this.state.popoverIsOpen) { return; }
      this.open();
    }
  };

  buttonMouseLeave = () => {
    const closed = this.isControlledComponent()
      ? !this.props.open
      : !this.state.popoverIsOpen;

    if (closed) { return; }
    this.newHoverTimer = setTimeout(this.clearHoverTimer, HOVER_GRACE_PERIOD);
  };

  componentWillUnmount() {
    const {onResize = NO_OP, id} = this.props;

    onResize(id, false);

    if (this.newHoverTimer) {
      clearTimeout(this.newHoverTimer);
    }
  }

  containerMouseMove = (event) => {
    event.stopPropagation();
  };

  curtainMouseMove = (event) => {
    event.stopPropagation();
    event.preventDefault();

    if (this.newHoverTimer !== null) { return; }

    if (this.isControlledComponent()) {
      if (!this.props.open) { return; }
      const {onShouldClose = NO_OP, id} = this.props;
      onShouldClose(id);
    } else {
      if (!this.state.popoverIsOpen) { return; }
      this.close();
    }
  };

  cancel = () => {
    if (this.isControlledComponent()) { return; }
    this.setState({popoverIsOpen: false});
  };

  close = () => {
    const {onClose = NO_OP} = this.props;
    this.cancel();
    onClose(this.props, this.state);
  };

  open = () => {
    this.setState({popoverIsOpen: true});
    this.afterOpen();
  };

  handleButtonClick = () => {
    if (this.isControlledComponent()) {
      const {id, open, onButtonClick = NO_OP} = this.props;
      onButtonClick(open ? '' : id);
    } else {
      const {popoverIsOpen} = this.state;
      this[popoverIsOpen ? 'close' : 'open']();
    }
  };

  isControlledComponent = () => {
    return this.props.hasOwnProperty('open');
  };

  render() {
    const {popoverIsOpen} = this.state;
    const {
      open,
      enabled,
      forceActiveState,
      children,
      label,
      className = '',
    } = this.props;

    return (
      <Measure bounds margin onResize={this.handleResize}>
      {({measureRef}) => (
        <div ref={measureRef} className={classNames(style.container, className)} onMouseMove={this.containerMouseMove}>
          <button
            type="button"
            onClick={this.handleButtonClick}
            disabled={!enabled}
            className={classNames(style.button, {
              [style.activeButton]: forceActiveState || open || popoverIsOpen,
              [style.loadingButton]: !enabled,
            })}
            onMouseMove={this.buttonMouseMove}
            onMouseLeave={this.buttonMouseLeave}
          >{label}</button>
            { (open || popoverIsOpen) ? (
              <div
                className={style.curtain}
                onMouseMove={this.curtainMouseMove}
              />
            ) : null }
          <div
            style={{display: (open || popoverIsOpen) ? 'block' : 'none'}}
            className={classNames(style.popover)}
          >{children}</div>
        </div>
      )}
      </Measure>
    );
  }
}

export default Popover;
